import { Icon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import * as Popover from '@radix-ui/react-popover'
import ReclaimAnswerModal, { AnswerActionId } from 'components/reclaims/ReclaimAnswerModal'
import ReclaimDecidedActionModal, {
    DecidedActionId,
} from 'components/reclaims/ReclaimDecidedActionModal'
import { ReclaimActionContext } from 'interfaces/Reclaims'
import { useState } from 'react'

type IconColor = 'green' | 'orange' | 'red' | 'grey'

// Answer actions reuse the open-reclaim flow; decided actions use the CS override endpoints.
type ActionId = AnswerActionId | DecidedActionId

interface ActionItem {
    id: ActionId
    label: string
    icon: string
    color: IconColor
}

const ANSWER_ACTIONS: AnswerActionId[] = ['approve', 'approveAlt', 'reject']

const ITEMS: Record<ActionId, ActionItem> = {
    approve: { id: 'approve', label: 'Approve', icon: 'check_circle', color: 'green' },
    approveAlt: {
        id: 'approveAlt',
        label: 'Approve outside policy',
        icon: 'offline_pin',
        color: 'orange',
    },
    reject: { id: 'reject', label: 'Reject', icon: 'cancel', color: 'red' },
    overrideApprove: {
        id: 'overrideApprove',
        label: 'Approve',
        icon: 'check_circle',
        color: 'green',
    },
    withdraw: { id: 'withdraw', label: 'Withdraw', icon: 'undo', color: 'grey' },
    delete: { id: 'delete', label: 'Delete', icon: 'delete', color: 'red' },
}

/** Build the list of actions valid for the reclaim's current state. */
const getAvailableActions = (reclaim: ReclaimActionContext) => {
    if (reclaim.deleted) {
        return [ITEMS.overrideApprove]
    }
    if (reclaim.is_open || reclaim.is_pending_ai_processing) {
        return [ITEMS.approve, ITEMS.approveAlt, ITEMS.reject, ITEMS.withdraw, ITEMS.delete]
    }
    if (reclaim.accepted) {
        return [ITEMS.withdraw, ITEMS.delete]
    }
    if (reclaim.denied || reclaim.withdrawn) {
        return [ITEMS.overrideApprove, ITEMS.delete]
    }
    return [ITEMS.delete]
}

const isAnswerAction = (action: ActionId): action is AnswerActionId =>
    ANSWER_ACTIONS.includes(action as AnswerActionId)

interface Props {
    reclaim: ReclaimActionContext
    onDone?: () => void
    // Smaller "Actions" button to fit inside the reclaims list "action" column.
    compact?: boolean
    // Icon-only trigger (e.g. for the matches/phone-clicks list reclaim cell).
    iconTrigger?: boolean
    // Restrict the menu to the Withdraw and Delete actions. Used for rows that are otherwise
    // locked (e.g. an automated check email is still out), so CS can still withdraw or delete.
    locked?: boolean
}

// Actions still allowed on a locked row (see `locked` prop).
const LOCKED_ROW_ACTIONS: ActionId[] = ['withdraw', 'delete']

const ReclaimActionsMenu = ({
    reclaim,
    onDone,
    compact = false,
    iconTrigger = false,
    locked = false,
}: Props) => {
    const [menuOpen, setMenuOpen] = useState(false)

    const allActions = getAvailableActions(reclaim)
    const actions = locked
        ? allActions.filter((item) => LOCKED_ROW_ACTIONS.includes(item.id))
        : allActions

    if (actions.length === 0) {
        return null
    }

    const openAction = (action: ActionId) => {
        setMenuOpen(false)
        if (isAnswerAction(action)) {
            NiceModal.show(ReclaimAnswerModal, {
                reclaim,
                answer: action,
                onSuccess: onDone,
            })
            return
        }
        NiceModal.show(ReclaimDecidedActionModal, {
            reclaimId: reclaim.reclaim_id,
            action,
            onSuccess: onDone,
        })
    }

    return (
        <div className="pointer-events-auto inline-flex">
            <Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
                <Popover.Trigger asChild>
                    {iconTrigger ? (
                        <button
                            type="button"
                            title="Reclaim actions"
                            className="inline-flex items-center justify-center rounded border border-solid border-grey-500 bg-white p-0.5 text-primary-500 hover:bg-grey-200">
                            <Icon name="bolt" fontSize={18} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={
                                compact
                                    ? 'inline-flex items-center gap-0.5 rounded border border-solid border-grey-500 bg-white px-1.5 py-0.5 text-sm font-medium hover:bg-grey-200'
                                    : 'inline-flex items-center gap-1 rounded-md border border-solid border-grey-500 bg-white px-3 py-2 text-md font-medium hover:bg-grey-200'
                            }>
                            Actions
                            <Icon name="expand_more" fontSize={compact ? 16 : 20} />
                        </button>
                    )}
                </Popover.Trigger>
                <Popover.Portal>
                    <Popover.Content
                        align="end"
                        sideOffset={4}
                        className="z-50 min-w-[220px] rounded-md border border-solid border-grey-500 bg-white p-1 shadow-md">
                        <div className="flex flex-col">
                            {actions.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => openAction(item.id)}
                                    className="flex items-center gap-2 rounded px-3 py-2 text-left text-md hover:bg-grey-200">
                                    <Icon
                                        name={item.icon}
                                        green={item.color === 'green'}
                                        orange={item.color === 'orange'}
                                        red={item.color === 'red'}
                                        grey={item.color === 'grey'}
                                        fontSize={20}
                                    />
                                    <span>{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    )
}

export default ReclaimActionsMenu
